import { PluginKey } from '@tiptap/pm/state';
import { Extension } from '@tiptap/react';
import Suggestion, {
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';
import type { RefObject } from 'react';
import type { LinkedContextItem } from '../../contracts/projects/linked-context';

import { chatObjectTokenAttrsForLinkedItem } from './projects-composer-editor-draft';
import {
  textBeforeComposerCaret,
  textPrefersPatientMention,
} from './projects-composer-mention-preference';
import { exitComposerSuggestion } from './projects-composer-suggestion-dismiss';

const PROJECTS_CONTEXT_SUGGESTION_NAME = 'projects-context-suggestion';

export const projectsContextSuggestionPluginKey = new PluginKey(
  PROJECTS_CONTEXT_SUGGESTION_NAME
);

export interface ProjectsContextSuggestionSession {
  readonly query: string;
  readonly items: readonly LinkedContextItem[];
  readonly select: (item: LinkedContextItem) => void;
  readonly dismiss: () => void;
}

export interface ProjectsContextSuggestionOptions {
  readonly getItems: () => readonly LinkedContextItem[];
  readonly onSelect: (item: LinkedContextItem) => void;
  readonly onSessionChange: (session: ProjectsContextSuggestionSession | null) => void;
  readonly keyDownRef: RefObject<((props: SuggestionKeyDownProps) => boolean) | null>;
  readonly itemLimit?: number;
  /**
   * True inserts an inline object token. Otherwise main's pick: the `@query`
   * is deleted and the host links the item as a chip.
   */
  readonly insertsToken?: () => boolean;
}

export function createProjectsContextSuggestion(
  options: ProjectsContextSuggestionOptions
): Extension {
  return Extension.create<ProjectsContextSuggestionOptions>({
    name: PROJECTS_CONTEXT_SUGGESTION_NAME,

    addProseMirrorPlugins() {
      return [
        Suggestion<LinkedContextItem, LinkedContextItem>({
          editor: this.editor,
          pluginKey: projectsContextSuggestionPluginKey,
          char: '@',
          allowSpaces: false,
          startOfLine: false,

          items: ({ query, editor }) => {
            const limit = options.itemLimit ?? 8;
            const filtered = filterProjectsContextSuggestions(
              options.getItems(),
              query,
              limit
            );
            if (!textPrefersPatientMention(textBeforeComposerCaret(editor))) {
              return filtered;
            }
            const patients = filtered.filter((item) => item.type === 'patient');
            return patients.length > 0 ? patients : filtered;
          },

          command: ({ editor, range, props: item }) => {
            if (!options.insertsToken?.()) {
              editor
                .chain()
                .focus(undefined, { scrollIntoView: false })
                .deleteRange(range)
                .run();
              options.onSelect(item);
              return;
            }
            editor
              .chain()
              .focus(undefined, { scrollIntoView: false })
              .deleteRange(range)
              .insertChatObjectToken(chatObjectTokenAttrsForLinkedItem(item))
              .run();
            options.onSelect(item);
          },

          render: () => ({
            onStart: (props) => {
              options.onSessionChange(buildContextSession(props));
            },
            onUpdate: (props) => {
              options.onSessionChange(buildContextSession(props));
            },
            onKeyDown: (props) => options.keyDownRef.current?.(props) ?? false,
            onExit: () => {
              options.onSessionChange(null);
            },
          }),
        }),
      ];
    },
  });
}

export function filterProjectsContextSuggestions(
  items: readonly LinkedContextItem[],
  query: string,
  limit: number
): LinkedContextItem[] {
  const q = query.trim().toLowerCase();
  const matches = (item: LinkedContextItem): boolean => {
    if (q.length === 0) return true;
    if (item.type === 'session') {
      return [item.sessionName, item.patientName]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(q));
    }
    if (item.type === 'library_document') {
      return item.filename.toLowerCase().includes(q);
    }
    if (item.type === 'task') {
      return [item.label, item.patientName].some((value) =>
        value?.toLowerCase().includes(q)
      );
    }
    if (item.type === 'template' || item.type === 'collection') {
      return item.label.toLowerCase().includes(q);
    }
    return item.patientName.toLowerCase().includes(q);
  };
  const matched = items.filter(matches);
  // Capped per type, in the caller's section order: a flat slice let recent
  // sessions fill the menu, so patients never appeared until the query happened
  // to miss every session.
  return [...new Set(matched.map((item) => item.type))].flatMap((type) =>
    matched.filter((item) => item.type === type).slice(0, limit)
  );
}

function buildContextSession(
  props: SuggestionProps<LinkedContextItem, LinkedContextItem>
): ProjectsContextSuggestionSession {
  return {
    query: props.query ?? '',
    items: props.items,
    select: (item) => props.command(item),
    dismiss: () =>
      exitComposerSuggestion(
        props.editor,
        projectsContextSuggestionPluginKey,
        props.range,
        '@'
      ),
  };
}
